import { SortingState } from "@tanstack/react-table";

import { TableColumnConfig } from "./TableConfigurationSystem";

/**
 * Extracts a value from an object using a field path (e.g., "category.name")
 */
function getValueByPath(
  obj: Record<string, unknown>,
  fieldPath: string,
): unknown {
  if (fieldPath.includes(".")) {
    const parts = fieldPath.split(".");
    return parts.reduce(
      (current: Record<string, unknown>, key) =>
        current?.[key] as Record<string, unknown>,
      obj,
    );
  }
  return obj[fieldPath];
}

/**
 * Compares two values for sorting
 */
function compareValues(
  aValue: unknown,
  bValue: unknown,
  desc: boolean,
): number {
  // Handle null/undefined values
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return 1;
  if (bValue == null) return -1;

  // Handle string comparison
  if (typeof aValue === "string" && typeof bValue === "string") {
    const comparison = aValue.localeCompare(bValue);
    return desc ? -comparison : comparison;
  }

  // Handle numeric comparison
  if (typeof aValue === "number" && typeof bValue === "number") {
    return desc ? bValue - aValue : aValue - bValue;
  }

  // Default string conversion comparison
  const aStr = String(aValue).toLowerCase();
  const bStr = String(bValue).toLowerCase();
  const comparison = aStr.localeCompare(bStr);
  return desc ? -comparison : comparison;
}

/**
 * Sorts an array of data using table configuration and sorting state
 * This performs client-side sorting to maintain grouping of nested resources
 */
export function sortDataWithGrouping<TData>(
  data: TData[],
  columns: Record<string, TableColumnConfig<unknown, string>>,
  sorting: SortingState,
): TData[] {
  if (sorting.length === 0) {
    return data;
  }

  const sorted = [...data];
  const sortConfig = sorting[0]; // Take first sort configuration
  const { id: columnId, desc } = sortConfig;

  const columnConfig = columns[columnId];

  if (columnConfig?.fieldPath) {
    sorted.sort((a, b) => {
      const aValue = getValueByPath(
        a as Record<string, unknown>,
        columnConfig.fieldPath!,
      );
      const bValue = getValueByPath(
        b as Record<string, unknown>,
        columnConfig.fieldPath!,
      );

      return compareValues(aValue, bValue, desc);
    });
  }

  return sorted;
}

/**
 * Transforms primary resources into a flat array with nested resources grouped under their parents
 * Applies sorting while maintaining parent-child relationships
 */
export function buildGroupedTableData<
  TPrimaryResource extends { id: string },
  TNestedResource extends { id: string },
  TTableRow extends TPrimaryResource & {
    isNestedResource?: boolean;
    parentResourceId?: string;
  },
>(
  primaryResources: TPrimaryResource[],
  expandedRows: Set<string>,
  expandedRowsData: Map<string, TNestedResource[]>,
  columns: Record<string, TableColumnConfig<unknown, string>>,
  sorting: SortingState,
): TTableRow[] {
  // Sort primary resources using the current sorting configuration
  const sortedPrimaryResources = sortDataWithGrouping(
    primaryResources,
    columns,
    sorting,
  );

  // Build the final data array with nested resources grouped correctly
  const sortedAndGroupedData: TTableRow[] = [];

  sortedPrimaryResources.forEach((resource) => {
    // Add the primary resource
    sortedAndGroupedData.push({
      ...resource,
      isNestedResource: false,
    } as TTableRow);

    // Add nested resources if this row is expanded (they stay with their parent)
    if (expandedRows.has(resource.id)) {
      const nestedResources = expandedRowsData.get(resource.id) || [];
      nestedResources.forEach((nestedResource) => {
        sortedAndGroupedData.push({
          ...(nestedResource as unknown as TTableRow),
          isNestedResource: true,
          parentResourceId: resource.id,
        });
      });
    }
  });

  return sortedAndGroupedData;
}
